import PropTypes from 'prop-types';
import {
  GridAddIcon,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarFilterButton
} from '@mui/x-data-grid';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Box, Dialog, DialogActions, DialogContent, IconButton } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import { useIntl } from 'react-intl';
import { useState } from 'react';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContentText from '@mui/material/DialogContentText';
import MDButton from 'components/MDButton';

/**
 * CustomToolbar component for the DataGrid, providing buttons for adding, deleting,
 * and duplicating AI Models, as well as standard toolbar functionalities.
 */
function AiModelCustomToolbar({ onAdd, onDelete, onDuplicate, selectedRows }) {
  const { formatMessage: msg } = useIntl();

  const oneSelected = selectedRows.length === 1;
  const manySelected = selectedRows.length > 1;

  const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = useState(false);

  const handleDelete = () => {
    if (selectedRows && selectedRows.length > 0) {
      setConfirmDeleteDialogOpen(true);
    }
  };
  const handleConfirmDelete = async () => {
    setConfirmDeleteDialogOpen(false);
    onDelete();
  };

  const handleCancelDelete = () => {
    setConfirmDeleteDialogOpen(false);
  };

  return (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarDensitySelector />
      <Box sx={{ flexGrow: 1 }} />
      <Tooltip title={msg({ id: `action.create` })}>
        <IconButton
          aria-label="add"
          color="success"
          onClick={onAdd}
          type="button"
          sx={{ mr: -1 }}
          size="large"
        >
          <GridAddIcon fontSize="inherit" />
        </IconButton>
      </Tooltip>
      <Tooltip title={msg({ id: `action.delete` })}>
        <span>
          <IconButton
            type="button"
            aria-label="delete"
            color="error"
            sx={{ mr: -1 }}
            size="large"
            onClick={handleDelete}
            disabled={!oneSelected && !manySelected}
          >
            <DeleteIcon />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title={msg({ id: `action.duplicate` })}>
        <span>
          <IconButton
            type="button"
            aria-label="duplicate"
            color="primary"
            size="large"
            onClick={onDuplicate}
            disabled={!oneSelected || manySelected}
          >
            <ContentCopyIcon />
          </IconButton>
        </span>
      </Tooltip>
      <Dialog open={confirmDeleteDialogOpen} onClose={handleCancelDelete}>
        <DialogTitle>
          {msg({ id: 'dialog.delete.title', defaultMessage: 'Confirmation de suppression' })}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {msg(
              {
                id: 'dialog.delete.count.confirm.message'
              },
              { itemsCount: selectedRows?.length || 0 }
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={handleCancelDelete} color="secondary">
            {msg({ id: 'button.cancel', defaultMessage: 'Annuler' })}
          </MDButton>
          <MDButton onClick={handleConfirmDelete} color="error">
            {msg({ id: 'button.delete', defaultMessage: 'Supprimer' })}
          </MDButton>
        </DialogActions>
      </Dialog>
    </GridToolbarContainer>
  );
}

AiModelCustomToolbar.propTypes = {
  onAdd: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onDuplicate: PropTypes.func.isRequired,
  selectedRows: PropTypes.array.isRequired
};

export default AiModelCustomToolbar;
